import moment from 'moment';
import omit from 'lodash/omit';
import mixpanel from 'mixpanel-browser';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import {
  getDefaultTimeZoneOnBrowser,
  getStartOf,
  getStartOfWeek,
  monthIdString,
  parseDateFromISO8601,
  stringifyDateToISO8601,
} from '../../util/dates';
import { uniqueBy } from '../../util/generators';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { cancelProSignupEmails, createAlgoliaData, createDealApi, createOrUpdateListing, createPolicyPdf, getfetchAIResumeUpdate, getParentJobRole, notifyUsers, pauseReminderNotifyToUser, policyUrlMongoDb, saveResumeUrlMongoDb, scheduleHalfwayProfileEmail, scheduleInitialSearchEmail, sendGridEmail, updateAlgoliaData, updateListingMetaDataApi, updateMetaDataApi, mailerLiteSyncRole, mailerLiteSyncCompanyRole } from '../../util/api';
import { LISTING_PAGE_PARAM_TYPE_DRAFT, LISTING_PAGE_PARAM_TYPE_EDIT, parse } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { updateWallet } from '../CheckoutPage/CheckoutPage.duck';
import { LISTING_STATE_PUBLISHED } from '../../util/types';
import { ACCOUNT_TYPE } from '../../util/constants';
import { createResourceLocatorString } from '../../util/routes';
import { updateProfilePayload } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { fetchMatchedListings } from '../ManageListingsPage/ManageListingsPage.duck';

const { UUID } = sdkTypes;
const uploadURL = process.env.REACT_APP_CLOUDINARY_URL;

// Create array of N items where indexing starts from 1
const getArrayOfNItems = n =>
  Array(n)
    .fill()
    .map((v, i) => i + 1)
    .slice(1);

// Return an array of image ids
const imageIds = images => {
  // For newly uploaded image the UUID can be found from "img.imageId"
  // and for existing listing images the id is "img.id"
  return images ? images.map(img => img.imageId || img.id) : null;
};

// After listing creation & update, we want to make sure that uploadedImages state is cleaned
const updateUploadedImagesState = (state, payload) => {
  const { uploadedImages, uploadedImagesOrder } = state;

  // Images attached to listing entity
  const attachedImages = payload?.data?.relationships?.images?.data || [];
  const attachedImageUUIDStrings = attachedImages.map(img => img.id.uuid);

  // Uploaded images (which are propably not yet attached to listing)
  const unattachedImages = Object.values(state.uploadedImages);
  const duplicateImageEntities = unattachedImages.filter(unattachedImg =>
    attachedImageUUIDStrings.includes(unattachedImg.imageId?.uuid)
  );
  return duplicateImageEntities.length > 0
    ? {
      uploadedImages: {},
      uploadedImagesOrder: [],
    }
    : {
      uploadedImages,
      uploadedImagesOrder,
    };
};

const getImageVariantInfo = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const fieldsImage = [`variants.default`, `variants.${variantPrefix}`, `variants.${variantPrefix}-2x`];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    },
  };
};

const sortExceptionsByStartTime = (a, b) => {
  return a.attributes.start.getTime() - b.attributes.start.getTime();
};

// When navigating through weekly calendar,
// we want to merge new week-related data (inProgres, error) to weeklyExceptionQueries hashmap.
const mergeToWeeklyExceptionQueries = (weeklyExceptionQueries, weekStartId, newDataProps) => {
  return weekStartId
    ? {
      weeklyExceptionQueries: {
        ...weeklyExceptionQueries,
        [weekStartId]: {
          ...weeklyExceptionQueries[weekStartId],
          ...newDataProps,
        },
      },
    }
    : {};
};
// When navigating through monthly calendar (e.g. when adding a new AvailabilityException),
// we want to merge new month-related data (inProgres, error) to monthlyExceptionQueries hashmap.
const mergeToMonthlyExceptionQueries = (monthlyExceptionQueries, monthId, newDataProps) => {
  return monthId
    ? {
      monthlyExceptionQueries: {
        ...monthlyExceptionQueries,
        [monthId]: {
          ...monthlyExceptionQueries[monthId],
          ...newDataProps,
        },
      },
    }
    : {};
};

const requestAction = actionType => params => ({ type: actionType, payload: { params } });

const successAction = actionType => result => ({ type: actionType, payload: result.data });

const errorAction = actionType => payload => ({ type: actionType, payload, error: true });

// ================ Action types ================ //

export const MARK_TAB_UPDATED = 'app/EditListingPage/MARK_TAB_UPDATED';
export const CLEAR_UPDATED_TAB = 'app/EditListingPage/CLEAR_UPDATED_TAB';

export const CREATE_LISTING_DRAFT_REQUEST = 'app/EditListingPage/CREATE_LISTING_DRAFT_REQUEST';
export const CREATE_LISTING_DRAFT_SUCCESS = 'app/EditListingPage/CREATE_LISTING_DRAFT_SUCCESS';
export const CREATE_LISTING_DRAFT_ERROR = 'app/EditListingPage/CREATE_LISTING_DRAFT_ERROR';

export const PUBLISH_LISTING_REQUEST = 'app/EditListingPage/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS = 'app/EditListingPage/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR = 'app/EditListingPage/PUBLISH_LISTING_ERROR';

export const UPDATE_LISTING_REQUEST = 'app/EditListingPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/EditListingPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/EditListingPage/UPDATE_LISTING_ERROR';

export const SHOW_LISTINGS_REQUEST = 'app/EditListingPage/SHOW_LISTINGS_REQUEST';
export const SHOW_LISTINGS_SUCCESS = 'app/EditListingPage/SHOW_LISTINGS_SUCCESS';
export const SHOW_LISTINGS_ERROR = 'app/EditListingPage/SHOW_LISTINGS_ERROR';

export const FETCH_EXCEPTIONS_REQUEST = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_REQUEST';
export const FETCH_EXCEPTIONS_SUCCESS = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const FETCH_EXCEPTIONS_ERROR = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_ERROR';
export const FETCH_EXTRA_EXCEPTIONS_SUCCESS =
  'app/EditListingPage/FETCH_EXTRA_AVAILABILITY_EXCEPTIONS_SUCCESS';

export const ADD_EXCEPTION_REQUEST = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_REQUEST';
export const ADD_EXCEPTION_SUCCESS = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_SUCCESS';
export const ADD_EXCEPTION_ERROR = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_ERROR';

export const DELETE_EXCEPTION_REQUEST = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_REQUEST';
export const DELETE_EXCEPTION_SUCCESS = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_SUCCESS';
export const DELETE_EXCEPTION_ERROR = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_ERROR';

export const SET_STOCK_REQUEST = 'app/EditListingPage/SET_STOCK_REQUEST';
export const SET_STOCK_SUCCESS = 'app/EditListingPage/SET_STOCK_SUCCESS';
export const SET_STOCK_ERROR = 'app/EditListingPage/SET_STOCK_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditListingPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditListingPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditListingPage/UPLOAD_IMAGE_ERROR';

export const REMOVE_LISTING_IMAGE = 'app/EditListingPage/REMOVE_LISTING_IMAGE';

export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_ERROR';

export const PARENT_JOB_ROLES_REQUEST = 'app/EditListingPage/PARENT_JOB_ROLES_REQUEST';
export const PARENT_JOB_ROLES_SUCCESS = 'app/EditListingPage/PARENT_JOB_ROLES_SUCCESS';
export const PARENT_JOB_ROLES_ERROR = 'app/EditListingPage/PARENT_JOB_ROLES_ERROR';

export const AI_RESUME_REQUEST = 'app/EditListingPage/AI_RESUME_REQUEST';
export const AI_RESUME_SUCCESS = 'app/EditListingPage/AI_RESUME_SUCCESS';
export const AI_RESUME_ERROR = 'app/EditListingPage/AI_RESUME_ERROR';


// ================ Reducer ================ //

const initialState = {
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  listingId: null,
  publishListingError: null,
  publishListingInProgress: false,
  updateListingError: null,
  showListingsError: null,
  uploadImageError: null,
  setStockError: null,
  setStockInProgress: false,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  uploadedImages: {},
  uploadInProgress: false,
  uploadedImagesOrder: [],
  removedImageIds: [],
  addExceptionError: null,
  addExceptionInProgress: false,
  weeklyExceptionQueries: {
    // '2022-12-12': { // Note: id/key is the start of the week in given time zone
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  monthlyExceptionQueries: {
    // '2022-12': {
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  allExceptions: [],
  deleteExceptionError: null,
  deleteExceptionInProgress: false,
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  parentJobRoles: null,
  parentJobRolesInProgress: false,
  aiResumeDataInProgress: false,
  aiResumeData: [],
  aiResumeDataError: null
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateListingError: null };

    case CREATE_LISTING_DRAFT_REQUEST:
      return {
        ...state,
        createListingDraftInProgress: true,
        createListingDraftError: null,
        submittedListingId: null,
        listingDraft: null,
      };

    case CREATE_LISTING_DRAFT_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        createListingDraftInProgress: false,
        submittedListingId: payload.data.id,
        listingDraft: payload.data,
      };
    case CREATE_LISTING_DRAFT_ERROR:
      return {
        ...state,
        createListingDraftInProgress: false,
        createListingDraftError: payload,
      };

    case PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        listingId: payload.listingId,
        publishListingError: null,
        publishListingInProgress: true,
      };
    case PUBLISH_LISTING_SUCCESS:
      return {
        ...state,
        redirectToListing: true,
        createListingDraftError: null,
        updateListingError: null,
        showListingsError: null,
        uploadImageError: null,
        createListingDraftInProgress: false,
        updateInProgress: false,
        publishListingInProgress: false,
      };
    case PUBLISH_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        publishListingInProgress: false,
        publishListingError: {
          listingId: state.listingId,
          error: payload,
        },
      };
    }

    case UPDATE_LISTING_REQUEST:
      return { ...state, updateInProgress: true, updateListingError: null };
    case UPDATE_LISTING_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        updateInProgress: false,
        // availabilityCalendar: { ...state.availabilityCalendar },
      };
    case UPDATE_LISTING_ERROR:
      return { ...state, updateInProgress: false, updateListingError: payload };

    case SHOW_LISTINGS_REQUEST:
      return { ...state, showListingsError: null };
    case SHOW_LISTINGS_SUCCESS: {
      const listingIdFromPayload = payload.data.id;
      const { listingId, allExceptions, weeklyExceptionQueries, monthlyExceptionQueries } = state;
      // If listing stays the same, we trust previously fetched exception data.
      return listingIdFromPayload?.uuid === state.listingId?.uuid
        ? {
          ...initialState,
          listingId,
          allExceptions,
          weeklyExceptionQueries,
          monthlyExceptionQueries,
        }
        : { ...initialState, listingId: listingIdFromPayload };
    }
    case SHOW_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, showListingsError: payload, redirectToListing: false };

    case FETCH_EXCEPTIONS_REQUEST: {
      const { monthId, weekStartId } = payload.params;
      const newData = { fetchExceptionsError: null, fetchExceptionsInProgress: true };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
          ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
          : {};
      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_SUCCESS: {
      const { exceptions, monthId, weekStartId } = payload;
      const combinedExceptions = state.allExceptions.concat(exceptions);
      const selectId = x => x.id.uuid;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(sortExceptionsByStartTime);
      const newData = { fetchExceptionsInProgress: false };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
          ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
          : {};
      return { ...state, allExceptions, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_ERROR: {
      const { monthId, weekStartId, error } = payload;
      const newData = { fetchExceptionsInProgress: false, fetchExceptionsError: error };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
          ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
          : {};

      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXTRA_EXCEPTIONS_SUCCESS: {
      const combinedExceptions = state.allExceptions.concat(payload.exceptions);
      const selectId = x => x.id.uuid;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(sortExceptionsByStartTime);
      // TODO: currently we don't handle thrown errors from these paginated calls
      return { ...state, allExceptions };
    }
    case ADD_EXCEPTION_REQUEST:
      return {
        ...state,
        addExceptionError: null,
        addExceptionInProgress: true,
      };
    case ADD_EXCEPTION_SUCCESS: {
      const exception = payload;
      const combinedExceptions = state.allExceptions.concat(exception);
      const allExceptions = combinedExceptions.sort(sortExceptionsByStartTime);
      return {
        ...state,
        allExceptions,
        addExceptionInProgress: false,
      };
    }
    case ADD_EXCEPTION_ERROR:
      return {
        ...state,
        addExceptionError: payload.error,
        addExceptionInProgress: false,
      };

    case DELETE_EXCEPTION_REQUEST:
      return {
        ...state,
        deleteExceptionError: null,
        deleteExceptionInProgress: true,
      };
    case DELETE_EXCEPTION_SUCCESS: {
      const exception = payload;
      const id = exception.id.uuid;
      const allExceptions = state.allExceptions.filter(e => e.id.uuid !== id);
      return {
        ...state,
        allExceptions,
        deleteExceptionInProgress: false,
      };
    }
    case DELETE_EXCEPTION_ERROR:
      return {
        ...state,
        deleteExceptionError: payload.error,
        deleteExceptionInProgress: false,
      };

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const uploadedImages = {
        ...state.uploadedImages,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        uploadedImages,
        uploadInProgress: true,
        uploadedImagesOrder: state.uploadedImagesOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id', attributes, type }
      const { id, imageId, imageType, uploadedImage, secure_url } = payload;
      const file = state.uploadedImages[id].file;
      const uploadedImages = { ...state.uploadedImages, [id]: { id, imageId, file, secure_url, imageType }, [imageType]: { id, imageId, uploadedImage, file, secure_url } };
      return { ...state, uploadInProgress: false, uploadedImages };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);
      const uploadedImages = omit(state.uploadedImages, id);
      return { ...state, uploadInProgress: false, uploadedImagesOrder, uploadedImages, uploadImageError: error };
    }

    case REMOVE_LISTING_IMAGE: {
      const id = payload.imageId;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.uploadedImages[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const uploadedImages = omit(state.uploadedImages, id);
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);

      return { ...state, uploadedImages, uploadedImagesOrder, removedImageIds };
    }

    case SET_STOCK_REQUEST:
      return { ...state, setStockInProgress: true, setStockError: null };
    case SET_STOCK_SUCCESS:
      return { ...state, setStockInProgress: false };
    case SET_STOCK_ERROR:
      return { ...state, setStockInProgress: false, setStockError: payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case PARENT_JOB_ROLES_REQUEST:
      return { ...state, parentJobRolesInProgress: true };
    case PARENT_JOB_ROLES_SUCCESS:
      return { ...state, parentJobRolesInProgress: false, parentJobRoles: payload };
    case PARENT_JOB_ROLES_ERROR:
      return { ...state, parentJobRolesInProgress: false };

    case AI_RESUME_REQUEST:
      return { ...state, aiResumeDataInProgress: true, aiResumeDataError: null };
    case AI_RESUME_SUCCESS:
      return { ...state, aiResumeDataInProgress: false, aiResumeData: payload };
    case AI_RESUME_ERROR:
      return { ...state, aiResumeDataInProgress: false, aiResumeDataError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const markTabUpdated = tab => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

export const removeListingImage = imageId => ({
  type: REMOVE_LISTING_IMAGE,
  payload: { imageId },
});

// All the action creators that don't have the {Success, Error} suffix
// take the params object that the corresponding SDK endpoint method
// expects.

// SDK method: ownListings.create
export const createListingDraftRequest = requestAction(CREATE_LISTING_DRAFT_REQUEST);
export const createListingDraftSuccess = successAction(CREATE_LISTING_DRAFT_SUCCESS);
export const createListingDraftError = errorAction(CREATE_LISTING_DRAFT_ERROR);

// SDK method: ownListings.publish
export const publishListingRequest = requestAction(PUBLISH_LISTING_REQUEST);
export const publishListingSuccess = successAction(PUBLISH_LISTING_SUCCESS);
export const publishListingError = errorAction(PUBLISH_LISTING_ERROR);

// SDK method: ownListings.update
export const updateListingRequest = requestAction(UPDATE_LISTING_REQUEST);
export const updateListingSuccess = successAction(UPDATE_LISTING_SUCCESS);
export const updateListingError = errorAction(UPDATE_LISTING_ERROR);

// SDK method: ownListings.show
export const showListingsRequest = requestAction(SHOW_LISTINGS_REQUEST);
export const showListingsSuccess = successAction(SHOW_LISTINGS_SUCCESS);
export const showListingsError = errorAction(SHOW_LISTINGS_ERROR);

// SDK method: images.upload
export const uploadImageRequest = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);

// SDK method: stock.compareAndSet
export const setStockRequest = requestAction(SET_STOCK_REQUEST);
export const setStockSuccess = successAction(SET_STOCK_SUCCESS);
export const setStockError = errorAction(SET_STOCK_ERROR);

// SDK method: availabilityExceptions.query
export const fetchAvailabilityExceptionsRequest = requestAction(FETCH_EXCEPTIONS_REQUEST);
export const fetchAvailabilityExceptionsSuccess = successAction(FETCH_EXCEPTIONS_SUCCESS);
export const fetchAvailabilityExceptionsError = errorAction(FETCH_EXCEPTIONS_ERROR);
// Add extra data from additional pages
export const fetchExtraAvailabilityExceptionsSuccess = successAction(
  FETCH_EXTRA_EXCEPTIONS_SUCCESS
);

// SDK method: availabilityExceptions.create
export const addAvailabilityExceptionRequest = requestAction(ADD_EXCEPTION_REQUEST);
export const addAvailabilityExceptionSuccess = successAction(ADD_EXCEPTION_SUCCESS);
export const addAvailabilityExceptionError = errorAction(ADD_EXCEPTION_ERROR);

// SDK method: availabilityExceptions.delete
export const deleteAvailabilityExceptionRequest = requestAction(DELETE_EXCEPTION_REQUEST);
export const deleteAvailabilityExceptionSuccess = successAction(DELETE_EXCEPTION_SUCCESS);
export const deleteAvailabilityExceptionError = errorAction(DELETE_EXCEPTION_ERROR);

export const savePayoutDetailsRequest = requestAction(SAVE_PAYOUT_DETAILS_REQUEST);
export const savePayoutDetailsSuccess = successAction(SAVE_PAYOUT_DETAILS_SUCCESS);
export const savePayoutDetailsError = errorAction(SAVE_PAYOUT_DETAILS_ERROR);

export const parentJobRolesRequest = requestAction(PARENT_JOB_ROLES_REQUEST);
export const parentJobRolesSuccess = successAction(PARENT_JOB_ROLES_SUCCESS);
export const parentJobRolesError = errorAction(PARENT_JOB_ROLES_ERROR);

export const fetchAIResumeUpdateRequest = requestAction(AI_RESUME_REQUEST);
export const fetchAIResumeUpdateSuccess = successAction(AI_RESUME_SUCCESS);
export const fetchAIResumeUpdateError = errorAction(AI_RESUME_ERROR);

// ================ Thunk ================ //

export function requestShowListing(actionPayload, config) {
  return (dispatch, getState, sdk) => {
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(showListingsRequest(actionPayload));
    return sdk.ownListings
      .show({ ...actionPayload, ...queryParams })
      .then(response => {
        // EditListingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.EditListingPage (user will be redirected away)
        dispatch(showListingsSuccess(response));
        return response;
      })
      .catch(e => dispatch(showListingsError(storableError(e))));
  };
}

// Set stock if requested among listing update info
export function compareAndSetStock(listingId, oldTotal, newTotal) {
  return (dispatch, getState, sdk) => {
    dispatch(setStockRequest());

    return sdk.stock
      .compareAndSet({ listingId, oldTotal, newTotal }, { expand: true })
      .then(response => {
        // NOTE: compareAndSet returns the stock resource of the listing.
        // We update client app's internal state with these updated API entities.
        dispatch(addMarketplaceEntities(response));
        dispatch(setStockSuccess(response));
      })
      .catch(e => {
        log.error(e, 'update-stock-failed', { listingId, oldTotal, newTotal });
        return dispatch(setStockError(storableError(e)));
      });
  };
}

// Helper function to make compareAndSetStock call if stock update is needed.
const updateStockOfListingMaybe = (listingId, stockTotals, dispatch) => {
  const { oldTotal, newTotal } = stockTotals || {};
  // Note: newTotal and oldTotal must be given, but oldTotal can be null
  const hasStockTotals = newTotal >= 0 && typeof oldTotal !== 'undefined';

  if (listingId && hasStockTotals) {
    return dispatch(compareAndSetStock(listingId, oldTotal, newTotal));
  }
  return Promise.resolve();
};

// Create listing in draft state
// NOTE: we want to keep it possible to include stock management field to the first wizard form.
// this means that there needs to be a sequence of calls:
// create, set stock, show listing (to get updated currentStock entity)
export function requestCreateListingDraft(data, config) {
  return (dispatch, getState, sdk) => {
    dispatch(createListingDraftRequest(data));
    const { stockUpdate, images, ...rest } = data;

    // If images should be saved, create array out of the image UUIDs for the API call
    // Note: in this template, image upload is not happening at the same time as listing creation.
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingValues = { ...imageProperty, ...rest };

    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };
    const { currentUser } = getState().user;
    const { uuid: customerId } = (currentUser && currentUser.id) || {};
    const { email } = (currentUser && currentUser.attributes) || {};
    const {
      publicData: userPublicData
    } = currentUser.attributes.profile;
    const { pipedriveUserId, accountType } = userPublicData || {};
    const {
      title = "",
      description = "",
      publicData = {}
    } = data || {};
    const { people, portfolio, pipedriveDealId, jd, higherEducation, skillsInOne, addExtraExperience, educations,
      ...restPublicData } = publicData || {};
    const { experiences, roles, addSelfie1 } = restPublicData || {};
    const modifiedExperience = [];
    let dealMoney = 0;
    (experiences && experiences.length && Array.isArray(experiences)) ? experiences.map((data, i) => {
      if (data && data?.label && data?.inputValues && data?.inputValues.price) {
        if (i == 0) {
          dealMoney = (data?.inputValues.price) / 100;
        }
        modifiedExperience.push({ experienceRole: data?.label, earnedMoney: (data?.inputValues.price) / 100 })
      }
    }) : null;

    (roles && roles.length && Array.isArray(roles) ? roles.map((data, i) => {
      if (data && data.label && data.perHourRate) {
        if (i == 0) {
          dealMoney = (data.perHourRate.value) / 100;
        }
        modifiedExperience.push({
          experience
            : (data && data.experience) ? data.experience : []
          , perHourRate: (data.perHourRate) / 100, availableHoursPerWeek: (data && data.availableHoursPerWeek) ? data.availableHoursPerWeek : null
        })
      }
    }) : null)
    let createDraftResponse = null;
    return sdk.ownListings
      .createDraft(ownListingValues, queryParams)
      .then(response => {
        createDraftResponse = response;
        const listingId = response.data.data.id;
        // If stockUpdate info is passed through, update stock
        if (pipedriveUserId) {
          const dealData = {
            customerId,
            email,
            listingId: listingId?.uuid,
            accountType,
            title: accountType == ACCOUNT_TYPE.company ? title : description,
            modifiedExperience,
            dealMoney,
            pipedriveUserId,
            pipedriveDealId,
            addSelfie1,
            pipeline_id: config.jobPublishedPipeline,
            stage_id: config.jobPendingStageId,
            listing_complete: accountType == ACCOUNT_TYPE.company ? '25%' : '14%'
          }
          createDealApi(dealData)
            .then((res) => {
              const { id: pipedriveDealId } = (res && res.data && res.data.data) || {};
              if (pipedriveDealId) {
                const data = {
                  id: listingId?.uuid,
                  publicData: {
                    pipedriveDealId
                  }
                }
                sdk.ownListings.update(data, queryParams)
              }
            })

        }
        return updateStockOfListingMaybe(listingId, stockUpdate, dispatch);
      })
      .then(() => {
        // Modify store to understand that we have created listing and can redirect away
        dispatch(createListingDraftSuccess(createDraftResponse));
        return createDraftResponse;
      })
      .catch(e => {
        log.error(e, 'create-listing-draft-failed', { listingData: data });
        return dispatch(createListingDraftError(storableError(e)));
      });
  };
}

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
// NOTE: what comes to stock management, this follows the same pattern used in create listing call
export function requestUpdateListing(tab, data, config, doItLater = true) {
  return (dispatch, getState, sdk) => {
    if (doItLater) {
      dispatch(updateListingRequest(data));
    }
    const { id, stockUpdate, images, ...rest } = data;

    // If images should be saved, create array out of the image UUIDs for the API call
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingUpdateValues = { id, ...imageProperty, ...rest };
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    const state = getState();
    const { currentUser } = getState().user;
    const { uuid: customerId } = (currentUser && currentUser.id) || {};
    const { email, profile } = (currentUser && currentUser.attributes) || {};
    const { metadata } = profile || {}
    const {
      firstName,
      lastName,
      displayName,
      publicData: userPublicData
    } = currentUser.attributes.profile;
    const { pipedriveUserId, accountType } = userPublicData || {};

    if (userPublicData.location && userPublicData.location.selectedPlace && userPublicData.location.selectedPlace.address) {
      if (ownListingUpdateValues.publicData) {
        Object.assign(ownListingUpdateValues, {
          geolocation: userPublicData.location.selectedPlace.origin,
        });
        Object.assign(ownListingUpdateValues.publicData, {
          location: {
            address: userPublicData.location.selectedPlace.address
          }
        });
      } else {
        Object.assign(ownListingUpdateValues, {
          geolocation: userPublicData.location.selectedPlace.origin,
          publicData: {
            location: {
              address: userPublicData.location.selectedPlace.address
            }
          }
        });
      }
    }
    Object.assign(ownListingUpdateValues.publicData, { approvedByAdmin: true });
    const existingTimeZone =
      state.marketplaceData.entities.ownListing[id.uuid]?.attributes?.availabilityPlan?.timezone;
    const includedTimeZone = rest?.availabilityPlan?.timezone;

    // Note: if update values include stockUpdate, we'll do that first
    // That way we get updated currentStock info among ownListings.update
    return updateStockOfListingMaybe(id, stockUpdate, dispatch)
      .then(() => sdk.ownListings.update(ownListingUpdateValues, queryParams))
      .then(response => {
        dispatch(updateListingSuccess(response));
        dispatch(addMarketplaceEntities(response));
        dispatch(markTabUpdated(tab));

        if (images && Array.isArray(images)) {
          const addSelfieImage = images.find(img => img.file);
          if (addSelfieImage && addSelfieImage.file) {
            sdk.images.upload({ image: addSelfieImage.file })
              .then(profileImageResp => {
                const profileImageId = profileImageResp.data.data.id;
                return dispatch(updateProfilePayload({ profileImageId }));
              })
              .then(() => {
                dispatch(fetchCurrentUser());
              });
          }
        }

        const {
          title = "",
          description = "",
          price,
          state,
          geolocation,
          availabilityPlan = '',
          publicData = {}
        } = response.data.data.attributes || {};
        const { people, portfolio, pipedriveDealId, jd, higherEducation, skillsInOne, addExtraExperience, educations,
          ...restPublicData } = publicData || {};
        const { experiences, roles, addSelfie1, skill } = restPublicData || {};
        const skills = { label: [] };
        const favouriteToolArray = { label: [] };
        const specialtiesArray = { label: [] };
        if (skillsInOne && skillsInOne.length) {
          skillsInOne.forEach(({ childSkills = [] }) => {
            if (childSkills && childSkills?.length) {
              childSkills?.forEach(({ label, favouriteTools = [], specialties
                = [] }) => {
                skills?.label.push(label);
                if (favouriteTools && favouriteTools?.length) {
                  favouriteTools?.forEach(({ label }) => {
                    favouriteToolArray?.label.push(label);
                  });
                }
                if (specialties && specialties?.length) {
                  specialties?.forEach(({ label }) => {
                    specialtiesArray?.label.push(label);
                  });
                }
              });
            }
          });
        }

        const modifiedExperience = [];
        let dealMoney = 0;
        (experiences && experiences.length && Array.isArray(experiences)) ? experiences.map((data, i) => {
          if (data && data?.label && data?.inputValues && data?.inputValues.price) {
            if (i == 0) {
              dealMoney = (data?.inputValues.price) / 100;
            }
            modifiedExperience.push({ experienceRole: data.label, earnedMoney: (data?.inputValues.price) / 100 })
          }
        }) : null;

        (roles && roles.length && Array.isArray(roles) ? roles.map((data, i) => {
          if (data && data.label && data.perHourRate) {
            if (i == 0) {
              dealMoney = (data.perHourRate.value) / 100;
            }
            modifiedExperience.push({
              experience: (data && data.experience) ? data.experience : [],
              perHourRate: (data.perHourRate) / 100,
              availableHoursPerWeek: (data && data.availableHoursPerWeek) ? data.availableHoursPerWeek : null,
            })
          }
        }) : null)
        Object.assign(publicData, { approvedByAdmin: true })
        const listing_complete = (accountType == ACCOUNT_TYPE.company) ? ((state == LISTING_STATE_PUBLISHED) ? '100%' : dealMoney ? '75%' : '50%') : (state == LISTING_STATE_PUBLISHED) ? '100%' : addSelfie1 ? '84%' : portfolio ? '70%' : higherEducation ? '56%' : dealMoney ? '42%' : experiences ? '28%' : '14%';

        if (state == LISTING_STATE_PUBLISHED) {
          const algoliaObj = {
            indexName: userPublicData.accountType == ACCOUNT_TYPE.fractionalPro
              ? process.env.REACT_APP_ALGOLIA_PRO_LISTING_INDEX
              : process.env.REACT_APP_ALGOLIA_COMPANY_LISTING_INDEX,
            objectID: id.uuid,
            _geoloc: geolocation && geolocation.lat ? geolocation : {},
            title, description, price, skills, favouriteToolArray, specialtiesArray,
            ...restPublicData,
            // Only include safe user fields for Algolia - DO NOT include email, firstName, lastName, phoneNumber
            user: { displayName, ...userPublicData }
          };
          if (availabilityPlan) {
            Object.assign(algoliaObj, { availabilityPlan });
          }
          if (id?.uuid) {
            try {

              updateAlgoliaData(algoliaObj);
            } catch (error) {
            }
          }
        }
        if (pipedriveUserId) {
          const dealData = {
            customerId,
            email,
            listingId: id?.uuid,
            accountType,
            title: accountType == ACCOUNT_TYPE.company ? title : description,
            modifiedExperience,
            dealMoney,
            pipedriveUserId,
            pipedriveDealId,
            addSelfie1,
            pipeline_id: config.jobPublishedPipeline,
            stage_id: (listing_complete == '100%') ? config.jobPublishedStageId : config.jobPendingStageId,
            listing_complete
          }
          createDealApi(dealData)
            .then((res) => {
              const { id: pipedriveDealId } = (res && res.data && res.data.data) || {};
              if (pipedriveDealId) {
                Object.assign(ownListingUpdateValues.publicData, { pipedriveDealId })
                sdk.ownListings.update(ownListingUpdateValues, queryParams)
              }
            })

        }

        // Sync Fractional user's role to MailerLite (fire-and-forget, exclusive groups)
        const primaryRole = experiences?.[0]?.label;
        if (accountType === ACCOUNT_TYPE.fractionalPro && email && primaryRole) {
          mailerLiteSyncRole({ email, role: primaryRole }).catch(err =>
            console.error('[MailerLite] role sync failed:', err)
          );
        }

        // Sync Company listing roles to MailerLite (fire-and-forget, additive groups — never removes)
        // Company desired roles are stored in publicData.roles (REQUIREMENTS tab), not experiences
        // if (accountType === ACCOUNT_TYPE.company && email && roles && roles.length) {
        //   const companyRoles = roles.map(r => r.label).filter(Boolean);
        //   if (companyRoles.length) {
        //     mailerLiteSyncCompanyRole({ email, roles: companyRoles }).catch(err =>
        //       console.error('[MailerLite] company role sync failed:', err)
        //     );
        //   }
        // }

        // If time zone has changed, we need to fetch exceptions again
        // since week and month boundaries might have changed.
        if (!!includedTimeZone && includedTimeZone !== existingTimeZone) {
          const searchString = '';
          const firstDayOfWeek = config.localization.firstDayOfWeek;
          const listing = response.data.data;
          fetchLoadDataExceptions(dispatch, listing, searchString, firstDayOfWeek);
        }

        return response;
      })
      .catch(e => {
        log.error(e, 'update-listing-failed', { listingData: data });
        return dispatch(updateListingError(storableError(e)));
      });
  };
}


export const createPolicyURLAction = (params) => (dispatch, getState, sdk) => {
  return createPolicyURL({
    ...params,
    sdk
  });
};

export async function createPolicyURL({
  userId,
  listingId,
  userName,
  email,
  sdk,
  type,
  tab,
  currentUser
}) {
  try {
    const response = await createPolicyPdf({
      userId,
      ...(listingId && { listingId }),
      userName,
      type
    });

    const termsAndConditionUrl = response?.termsAndConditionUrl || response?.url;
    if (termsAndConditionUrl) {
      if (!currentUser && !currentUser?.id) {
        sdk.currentUser.updateProfile({
          publicData: {
            termsAndConditionUrl
          }
        })
      } else {
        sdk.ownListings.update({
          id: listingId,
          publicData: {
            termsAndConditionUrl
          }
        })
      }
      await policyUrlMongoDb({
        userId,
        listingId,
        email,
        termsAndConditionUrl,
        type,
        tab
      });

      console.log('Policy URL saved to MongoDB.');
    } else {
      console.error('No URL received from PDF generation');
    }
  } catch (error) {
    console.error('Error in createPolicyURL:', error);
  }
};

export const requestPublishListingDraft = (listingId, config) => (dispatch, getState, sdk) => {
  dispatch(publishListingRequest(listingId));

  return sdk.ownListings
    .publishDraft({ id: listingId }, { expand: true })
    .then(async response => {
      const { currentUser } = getState().user;
      const { email, profile } = (currentUser && currentUser.attributes) || {};
      const { metadata } = profile || {};
      const { uuid: customerId } = (currentUser && currentUser.id) || {};
      const {
        firstName,
        lastName,
        displayName,
        publicData: userPublicData
      } = currentUser.attributes.profile;
      const { pipedriveUserId, accountType, survey_mail_send = false, isProProfileCompleted } = userPublicData || {};
      if (!survey_mail_send) {
        sendGridEmail({
          to: email,
          dynamic_template_data: {
            name: displayName
          },
          type: accountType == ACCOUNT_TYPE.fractionalPro ? 'SURVEY_MAIL_FRACTIONAL_PRO' : 'SURVEY_MAIL_COMPANY'
        }).then((res) => {

          const updateData = {
            publicData: {
              survey_mail_send: true
            }
          };
          sdk.currentUser.updateProfile(updateData);
        });
      }

      try {
        createPolicyURL({
          userId: currentUser.id.uuid,
          listingId: listingId.uuid,
          userName: displayName,
          email: currentUser.attributes.email,
          sdk,
          type: accountType,
          currentUser
        });
      } catch (error) {
        console.error('Error creating policy URL:', error);
      }


      if (accountType === ACCOUNT_TYPE.fractionalPro) {
        const updateData = {
          publicData: {
            isProListingPublished: true,
            listingPublishedAt: moment().unix(),
            searchActivity: false
          }
        };
        sdk.currentUser.updateProfile(updateData);
        dispatch(onCancelSendgridSignupEmail(currentUser?.id?.uuid));
      }
      if ([ACCOUNT_TYPE.company, ACCOUNT_TYPE.fractionalPro].includes(accountType)) {
        dispatch(onCancelSendgridListingEmail(listingId));

        sdk.ownListings.query({}).then((res) => {
          const totalListings = res?.data?.meta?.totalItems || 0;

          if (totalListings === 1) {
            if (accountType === ACCOUNT_TYPE.company) {
              const updateData = {
                publicData: {
                  isCompanyListingPublished: true,
                  listingPublishedAt: moment().unix(),
                  searchActivity: false
                }
              };
              sdk.currentUser.updateProfile(updateData);
            }
            // scheduleInitialSearchEmail({
            //   id: currentUser?.id?.uuid,
            //   firstName,
            //   email: currentUser?.attributes?.email,
            //   userType: accountType,
            //   listingTitle:res?.data?.data[0]?.attributes?.title,
            //   listingId:res?.data?.data[0]?.id?.uuid,
            // });
          } else {
            console.log(`[Company] Skipping initial search email — already has ${totalListings} listings.`);
          }
        })
      }
      const {
        title = "",
        description = "",
        price,
        state,
        geolocation,
        availabilityPlan = '',
        publicData = {}
      } = response.data.data.attributes || {};

      const isFractional = accountType === ACCOUNT_TYPE.fractionalPro;
      const isCompany = accountType === ACCOUNT_TYPE.company;

      if (isFractional || isCompany) {
        const listingTitle = isFractional ? response?.data?.data?.attributes?.publicData?.experiences?.[0]?.key : response?.data?.data?.attributes?.publicData?.roles?.[0]?.key || '';
        const firstName = displayName;

        const listingType = isFractional ? 'fractionalPro' : 'company';

        // notifyUsers({
        //   listingType,
        //   listingId: listingId.uuid,
        //   title: listingTitle,
        //   firstName,
        //   currentUserEmail: currentUser?.attributes?.email
        // },);
      }

      if (isCompany) {
        dispatch(fetchMatchedListings({ listings: response?.data?.data || {} }));
      }

      const { people, portfolio, jd, higherEducation, skillsInOne, addExtraExperience, pipedriveDealId, educations, ...restPublicData } = publicData || {}
      const { experiences, roles, addSelfie1 } = restPublicData || {};

      Object.assign(publicData, { approvedByAdmin: true })
      if (metadata && metadata.mailBatchId && !metadata?.alreadyDisableBatchId) {
        pauseReminderNotifyToUser({ batchId: (metadata?.mailBatchId), userId: (currentUser.id.uuid) })
      }
      const skills = { label: [] };
      const favouriteToolArray = { label: [] };
      const specialtiesArray = { label: [] };
      if (skillsInOne && skillsInOne.length) {
        skillsInOne.forEach(({ childSkills = [] }) => {
          if (childSkills && childSkills?.length) {
            childSkills?.forEach(({ label, favouriteTools = [], specialties
              = [] }) => {
              skills?.label.push(label);
              if (favouriteTools && favouriteTools?.length) {
                favouriteTools?.forEach(({ label }) => {
                  favouriteToolArray?.label.push(label);
                });
              }
              if (specialties && specialties?.length) {
                specialties?.forEach(({ label }) => {
                  specialtiesArray?.label.push(label);
                });
              }
            });
          }
        });
      }

      if (state == LISTING_STATE_PUBLISHED) {
        mixpanel.track('ListingCreated', {
          distinct_id: currentUser.id.uuid,
          user_id: currentUser.id.uuid,
          email,
          listingType: userPublicData.accountType == ACCOUNT_TYPE.fractionalPro ? 'PRO' : 'COMPANY',
          stepCompleted: userPublicData.stepCompleted,
          hiringRole: publicData.roles && publicData.roles.length && Array.isArray(publicData.roles)
            ? publicData.roles.map(r => r.label).join(', ')
            : '',
          hoursPerWeek: userPublicData.availableHoursPerWeek,
          hourlyRate: (price && price.amount ? (price.amount / 100) : 0),
          environment: (process.env && process.env.REACT_APP_ENV) || 'development',
        });

        const algoliaObj = {
          indexName: userPublicData.accountType == ACCOUNT_TYPE.fractionalPro
            ? process.env.REACT_APP_ALGOLIA_PRO_LISTING_INDEX
            : process.env.REACT_APP_ALGOLIA_COMPANY_LISTING_INDEX,
          objectID: listingId.uuid,
          _geoloc: geolocation && geolocation.lat ? geolocation : {},
          title, description, price, availabilityPlan, skills, favouriteToolArray, specialtiesArray,
          ...restPublicData,
          // Only include safe user fields for Algolia - DO NOT include email, firstName, lastName, phoneNumber
          user: { displayName, ...userPublicData },
        };
        const modifiedExperience = [];
        let dealMoney = 0;
        (experiences && experiences.length && Array.isArray(experiences)) ? experiences.map((data, i) => {
          if (data && data?.label && data?.inputValues && data?.inputValues.price) {
            if (i == 0) {
              dealMoney = (data?.inputValues.price) / 100;
            }
            modifiedExperience.push({ experienceRole: data?.label, earnedMoney: (data?.inputValues.price) / 100 })
          }
        }) : null;
        (roles && roles.length && Array.isArray(roles) ? roles.map((data, i) => {
          if (data && data.label && data.perHourRate) {
            if (i == 0) {
              dealMoney = (data.perHourRate.value) / 100;
            }
            modifiedExperience.push({
              experience
                : (data && data.experience) ? data.experience : []
              , perHourRate: (data?.perHourRate) / 100, availableHoursPerWeek: (data && data.availableHoursPerWeek) ? data?.availableHoursPerWeek : null
            })
          }
        }) : null)

        if (availabilityPlan) {
          Object.assign(algoliaObj, { availabilityPlan });
        }

        try {
          createAlgoliaData(algoliaObj);

          // Sync roles to MailerLite on first publish (fire-and-forget)
          const publishPrimaryRole = experiences?.[0]?.label;
          if (accountType === ACCOUNT_TYPE.fractionalPro && email && publishPrimaryRole) {
            mailerLiteSyncRole({ email, role: publishPrimaryRole }).catch(err =>
              console.error('[MailerLite] role sync on publish failed:', err)
            );
          }
          // Company desired roles are in publicData.roles (REQUIREMENTS tab), not experiences
          // if (accountType === ACCOUNT_TYPE.company && email && roles && roles.length) {
          //   const publishCompanyRoles = roles.map(r => r.label).filter(Boolean);
          //   if (publishCompanyRoles.length) {
          //     mailerLiteSyncCompanyRole({ email, roles: publishCompanyRoles }).catch(err =>
          //       console.error('[MailerLite] company role sync on publish failed:', err)
          //     );
          //   }
          // }

          if (pipedriveUserId) {
            const dealData = {
              customerId,
              email,
              listingId: listingId?.uuid,
              accountType,
              title: accountType == ACCOUNT_TYPE.company ? title : description,
              modifiedExperience,
              dealMoney,
              pipedriveUserId,
              addSelfie1,
              pipedriveDealId,
              pipeline_id: config.jobPublishedPipeline,
              stage_id: config.jobPublishedStageId,
              listing_complete: '100%'
            }
            createDealApi(dealData).then((res) => {
              const { id: pipedriveDealId } = (res && res.data && res.data.data) || {};
              if (pipedriveDealId) {
                const updateData = {
                  id: listingId?.uuid,
                  publicData: {
                    pipedriveDealId
                  }
                }
                sdk.ownListings.update(updateData);
              }
            })
          }
        } catch (error) {
        }
      }
      // Add the created listing to the marketplace data
      dispatch(addMarketplaceEntities(response));
      dispatch(publishListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(publishListingError(storableError(e)));
    });
};

// Images return imageId which we need to map with previously generated temporary id
export const requestImageUpload = (actionPayload) => async (dispatch, getState, sdk) => {
  const uploadPromises = [];
  if (actionPayload.files && actionPayload.files.length) {
    for await (const file of actionPayload.files) {
      const id = `${file.name}_${Date.now()}`;
      dispatch(uploadImageRequest({ id, file }));
      const queryParams = {
        expand: true,
        'fields.image': ['variants.default', 'variants.square-small', 'variants.square-small2x', 'variants.landscape-crop'],
      };
      uploadPromises.push(sdk.images
        .upload({ image: file }, queryParams)
        .then(resp => {
          const formData = new FormData();
          const fileType = file.type;
          formData.append('file', file);
          formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_PRESET);
          formData.append('resource_type', fileType);
          formData.append('api_key', process.env.REACT_APP_CLOUDINARY_API_KEY);
          // formData.append('api_secret', 'gAkFjUdQaidnsnEUadyQerGeT0A');

          return fetch(uploadURL, { method: 'POST', body: formData })
            .then(response => response.text())
            .then(data => {
              const res = JSON.parse(data);
              if (res && res.secure_url) {
                dispatch(uploadImageSuccess({
                  data: {
                    id, imageId: resp.data.data.id,
                    uploadedImage: resp.data.data,
                    imageType: actionPayload.imageType,
                    secure_url: res.secure_url
                  }
                }));
                return resp.data.data.id;
              }
            });
        })
        .catch(e => dispatch(uploadImageError({ id, error: storableError(e) }))));
    }
    return await Promise.all(uploadPromises);
  } else {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));
    const queryParams = {
      expand: true,
      'fields.image': ['variants.default', 'variants.square-small', 'variants.square-small2x', 'variants.landscape-crop'],
    };
    return sdk.images
      .upload({ image: actionPayload.file }, queryParams)
      .then(resp => {
        const { file } = actionPayload;
        const formData = new FormData();
        const fileType = file.type;
        const uploadedImageId = resp.data.data.id;
        formData.append('file', file);
        formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_PRESET);
        formData.append('resource_type', fileType);
        formData.append('api_key', process.env.REACT_APP_CLOUDINARY_API_KEY);
        // formData.append('api_secret', 'gAkFjUdQaidnsnEUadyQerGeT0A');

        return fetch(uploadURL, { method: 'POST', body: formData })
          .then(response => response.text())
          .then(async data => {
            const res = JSON.parse(data);
            if (res && res.secure_url) {
              dispatch(uploadImageSuccess({
                data: {
                  id, imageId: resp.data.data.id,
                  uploadedImage: resp.data.data,
                  imageType: actionPayload.imageType,
                  secure_url: res.secure_url
                }
              }));
              return uploadedImageId;
            }
          });
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  }
}

export const requestAddAvailabilityException = params => (dispatch, getState, sdk) => {
  dispatch(addAvailabilityExceptionRequest(params));

  return sdk.availabilityExceptions
    .create(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      return dispatch(addAvailabilityExceptionSuccess({ data: availabilityException }));
    })
    .catch(e => {
      dispatch(addAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};


export const updateRoleQuestion = params => (dispatch, getState, sdk) => {
  const { cloudinaryUrl, listingId, title, highestExperience, specialtiesArray, favouriteToolArray } = params;
  return sdk.ownListings.update({
    id: (listingId)
    , publicData: {
      chatGptQuestions: { url: cloudinaryUrl, title, highestExperience, specialtiesArray, favouriteToolArray }
    }
  }, {
    expand: true
  })

}
export const requestDeleteAvailabilityException = params => (dispatch, getState, sdk) => {
  dispatch(deleteAvailabilityExceptionRequest(params));

  return sdk.availabilityExceptions
    .delete(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      return dispatch(deleteAvailabilityExceptionSuccess({ data: availabilityException }));
    })
    .catch(e => {
      dispatch(deleteAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

export const requestFetchAvailabilityExceptions = params => (dispatch, getState, sdk) => {
  const { listingId, start, end, timeZone, page, isWeekly } = params;
  const fetchParams = { listingId, start, end };
  const timeUnitIdProp = isWeekly
    ? { weekStartId: stringifyDateToISO8601(start) }
    : { monthId: monthIdString(start, timeZone) };
  dispatch(fetchAvailabilityExceptionsRequest(timeUnitIdProp));

  return sdk.availabilityExceptions
    .query(fetchParams)
    .then(response => {
      const availabilityExceptions = denormalisedResponseEntities(response);

      // Fetch potential extra exceptions pagination pages per month.
      // In theory, there could be several pagination pages worth of exceptions,
      // if range is month and unit is 'hour': 31 days * 24 hour = 744 slots for exceptions.
      const totalPages = response.data.meta.totalPages;
      if (totalPages > 1 && !page) {
        const extraPages = getArrayOfNItems(totalPages);

        // It's unlikely that this code is reached with default units.
        // Note:
        //  - Firing multiple API calls might hit API rate limit
        //    (This is very unlikely with this query and 'hour' unit.)
        //  - TODO: this doesn't take care of failures of those extra calls
        Promise.all(
          extraPages.map(page => {
            return sdk.availabilityExceptions.query({ ...fetchParams, page });
          })
        ).then(responses => {
          const denormalizedFlatResults = (all, r) => all.concat(denormalisedResponseEntities(r));
          const exceptions = responses.reduce(denormalizedFlatResults, []);
          dispatch(
            fetchExtraAvailabilityExceptionsSuccess({
              data: { ...timeUnitIdProp, exceptions },
            })
          );
        });
      }

      return dispatch(
        fetchAvailabilityExceptionsSuccess({
          data: { ...timeUnitIdProp, exceptions: availabilityExceptions },
        })
      );
    })
    .catch(e => {
      return dispatch(
        fetchAvailabilityExceptionsError({ ...timeUnitIdProp, error: storableError(e) })
      );
    });
};

// Helper function for loadData call.
const fetchLoadDataExceptions = (dispatch, listing, search, firstDayOfWeek) => {
  const hasWindow = typeof window !== 'undefined';
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone = listing?.attributes?.availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  // Note: listing needs to have time zone set!
  if (hasWindow && listing.id && hasTimeZone) {
    const listingId = listing.id;
    // If the listing doesn't have availabilityPlan yet
    // use the defaul timezone
    const timezone = listing.attributes.availabilityPlan?.timezone || getDefaultTimeZoneOnBrowser();
    const todayInListingsTZ = getStartOf(new Date(), 'day', timezone);

    const locationSearch = parse(search);
    const selectedDate = locationSearch?.d
      ? parseDateFromISO8601(locationSearch.d, timezone)
      : todayInListingsTZ;
    const startOfWeek = getStartOfWeek(selectedDate, timezone, firstDayOfWeek);
    const prevWeek = getStartOf(startOfWeek, 'day', timezone, -7, 'days');
    const nextWeek = getStartOf(startOfWeek, 'day', timezone, 7, 'days');
    const nextAfterNextWeek = getStartOf(nextWeek, 'day', timezone, 7, 'days');

    const nextMonth = getStartOf(todayInListingsTZ, 'month', timezone, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', timezone, 1, 'months');

    const sharedData = { listingId, timeZone: timezone };

    // Fetch data for selected week and nearest weeks for WeeklyCalendar
    // Plus current month and month after that for EditListingAvailabilityForm
    //
    // NOTE: This is making 5 different Thunk calls, which update store 2 times each
    //       It would make sense to make on thunk function that fires 5 sdk calls/promises,
    //       but for the time being, it's clearer to push all the calls through
    //       requestFetchAvailabilityExceptions
    return Promise.all([
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: prevWeek,
          end: startOfWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: startOfWeek,
          end: nextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: nextWeek,
          end: nextAfterNextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: todayInListingsTZ,
          end: nextMonth,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: nextMonth,
          end: nextAfterNextMonth,
        })
      ),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

export const fetchParentJobRoles = () => async (dispatch, getState, sdk) => {
  dispatch(parentJobRolesRequest());
  try {
    const res = await getParentJobRole();
    if (res?.data) {
      dispatch(parentJobRolesSuccess(res));
      return res;
    }
  } catch (error) {
    dispatch(parentJobRolesError(storableError(error)));
  }
};

// cancel sendgrid signup email 
export const onCancelSendgridSignupEmail = (userId) => async (dispatch, getState, sdk) => {
  try {
    const user = await sdk.users.show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    });

    const currentMetadata = user?.data?.data?.attributes?.profile?.metadata || {};
    const scheduledEmails = currentMetadata?.scheduledSignupEmails || [];
    const now = moment().unix();

    if (!scheduledEmails.length) {
      console.log(" No scheduled emails found to cancel.");
      return;
    }

    const updatedScheduledEmails = [];

    for (const entry of scheduledEmails) {
      const [dayKey, batchId] = Object.entries(entry).find(
        ([key]) => key.startsWith("day")
      ) || [];

      if (!dayKey || !batchId) {
        updatedScheduledEmails.push(entry);
        continue;
      }

      const sendAt = entry.sendAt;
      if (sendAt < now) {
        updatedScheduledEmails.push(entry);
        continue;
      }

      try {
        const cancelled = await cancelProSignupEmails({ batchId });
        updatedScheduledEmails.push({
          [dayKey]: batchId,
          cancelled: cancelled ? true : false,
          cancelledAt: moment().unix()
        });

        console.log(`Cancelled ${dayKey} email for batchId: ${batchId}`);
      } catch (err) {
        console.error(`Error cancelling ${dayKey}:`, err?.response?.body || err.message);
        updatedScheduledEmails.push(entry);
      }
    }

    // Save updated metadata
    await updateMetaDataApi({
      id: userId,
      metadata: {
        ...currentMetadata,
        scheduledSignupEmails: updatedScheduledEmails,
      },
    });

  } catch (error) {
    console.error(" Error in cancelling SendGrid emails:", error);
  }
};


export const onCancelSendgridListingEmail = (listingId) => async (dispatch, getState, sdk) => {
  try {
    const listing = await sdk.listings.show({
      id: listingId,
    });
    const currentMetadata = listing?.data?.data?.attributes?.metadata || {};

    const scheduledDraftReminderEmails = currentMetadata?.scheduledDraftReminderEmails || [];
    const now = moment().unix();

    if (!scheduledDraftReminderEmails.length) {
      console.log(" No scheduled emails found to cancel.");
      return;
    }

    const updatedscheduledDraftReminderEmails = [];

    for (const entry of scheduledDraftReminderEmails) {
      const [dayKey, batchId] = Object.entries(entry).find(
        ([key]) => key.startsWith("day")
      ) || [];


      const sendAt = entry.sendAt;
      if (sendAt < now) {
        updatedscheduledDraftReminderEmails.push(entry);
        continue;
      }

      try {
        const cancelled = await cancelProSignupEmails({ batchId });
        updatedscheduledDraftReminderEmails.push({
          [dayKey]: batchId,
          cancelled: cancelled ? true : false,
          cancelledAt: moment().unix()
        });

        console.log(`Cancelled listing ${dayKey} email for batchId: ${batchId}`);
      } catch (err) {
        console.error(`Error cancelling listing email for  ${dayKey}:`, err?.response?.body || err.message);
        updatedscheduledDraftReminderEmails.push({
          [dayKey]: batchId,
          cancelled: false
        });
      }
    }

    // Save updated metadata
    await updateListingMetaDataApi({
      id: listingId,
      metadata: {
        ...currentMetadata,
        scheduledDraftReminderEmails: updatedscheduledDraftReminderEmails,
      },
    });

  } catch (error) {
    console.error(" Error in cancelling SendGrid emails:", error);
  }
};


export const onCancelSendgridSearchEmail = (userId) => async (dispatch, getState, sdk) => {
  try {
    const user = await sdk.users.show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    });

    const currentMetadata = user?.data?.data?.attributes?.profile?.metadata || {};
    const scheduledEmails = currentMetadata?.searchEmail || [];

    const now = moment().unix();

    if (!scheduledEmails.length) {
      console.log(" No scheduled emails found to cancel.");
      return;
    }

    const updatedScheduledEmails = [];

    for (const entry of scheduledEmails) {
      const [dayKey, batchId] = Object.entries(entry).find(
        ([key]) => key.startsWith("day")
      ) || [];

      if (!dayKey || !batchId) {
        updatedScheduledEmails.push(entry);
        continue;
      }

      const sendAt = entry.sendAt;

      if (sendAt < now) {
        updatedScheduledEmails.push(entry);
        continue;
      }

      try {
        const cancelled = await cancelProSignupEmails({ batchId });

        updatedScheduledEmails.push({
          [dayKey]: batchId,
          cancelled: cancelled ? true : false,
          cancelledAt: moment().unix()
        });

        console.log(`Cancelled ${dayKey} email for batchId: ${batchId}`);
      } catch (err) {
        console.error(`Error cancelling ${dayKey}:`, err?.response?.body || err.message);
        updatedScheduledEmails.push({
          [dayKey]: batchId,
          cancelled: false
        });
      }
    }

    // Save updated metadata
    await updateMetaDataApi({
      id: userId,
      metadata: {
        ...currentMetadata,
        searchEmail: updatedScheduledEmails,
      },
    });

  } catch (error) {
    console.error(" Error in cancelling SendGrid emails:", error);
  }
};



export const fetchAIResumeUpdate = (formData, routeConfiguration, history, currentUser, isPublished, listingId, userId, email) => async (dispatch, getState, sdk) => {
  dispatch(fetchAIResumeUpdateRequest());

  try {
    const maxAttempts = 20;
    const retryableStatuses = new Set([503]);
    let parsed;
    let lastError = null;
    let uplodedResumeUrl = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`[AI Resume] attempt ${attempt}/${maxAttempts} starting`);
      const res = await getfetchAIResumeUpdate(formData);
      const { status, text } = res || {};
      console.log(`[AI Resume] attempt ${attempt}/${maxAttempts} response status=${status}`);
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch (e) {
        parsed = null;
      }

      const isTimeoutMsg = parsed?.message && /timeout/i.test(parsed.message);
      const isNetwork = status === 0;
      const serverError = status >= 499;

      if (retryableStatuses.has(status) || isTimeoutMsg || isNetwork || serverError) {
        lastError = { status, message: parsed?.message || 'temporary_error' };
        if (attempt < maxAttempts) {
          const delayMs = 1000 * attempt;
          console.warn(`[AI Resume] transient issue (status=${status}, timeoutMsg=${!!isTimeoutMsg}, network=${isNetwork}). Retrying in ${delayMs}ms...`);
          await new Promise(r => setTimeout(r, delayMs));
          continue;
        }
      }

      if (status >= 400 && !retryableStatuses.has(status)) {
        const customError = {
          message: parsed?.message || 'Failed to process resume',
          originalError: parsed?.error || text,
          status,
        };
        dispatch(fetchAIResumeUpdateError(customError));
        return;
      }

      if (status >= 200 && status < 300) {
        console.log(`[AI Resume] succeeded on attempt ${attempt}/${maxAttempts}`);
        break;
      }
    }

    if (!parsed) {
      console.error('[AI Resume] failed after retries', lastError);
      dispatch(fetchAIResumeUpdateError({
        message: lastError?.message || 'AI analysis timed out. Please try again.',
        originalError: lastError,
      }));
      return;
    }

    if (parsed?.error) {
      console.error('AI Response Error:', parsed.error);
      const customError = {
        message: parsed.message,
        originalError: parsed.error,
      };

      dispatch(fetchAIResumeUpdateError(customError));
      return;
    }

    if (parsed?.ai) {
      try {
        const fileBlob = formData?.get?.('file');

        if (uploadURL && fileBlob) {
          const formData = new FormData();
          formData.append("file", fileBlob);
          formData.append("upload_preset", process.env.REACT_APP_CLOUDINARY_PRESET);
          formData.append("api_key", process.env.REACT_APP_CLOUDINARY_API_KEY);

          const tCloud = Date.now();

          try {
            const cloudResp = await fetch(uploadURL, {
              method: "POST",
              body: formData,
            });

            const cloudJson = await cloudResp.json();
            uplodedResumeUrl = cloudJson?.secure_url || cloudJson?.url || null;

            if (!uplodedResumeUrl) {
              throw new Error('No secure_url received from Cloudinary.');
            }
          } catch (uploadErr) {
            console.warn('[Cloudinary Upload] Failed:', uploadErr.message);
          } finally {
            console.log(tCloud, 'cloudinaryUpload');
          }
        }
      } catch (e) {
        console.warn('[AI Resume] Cloudinary Upload Block Failed:', e?.message || e);
      }

    }


    const { experiences, description, portfolio, higherEducation, educations, childSkills } = parsed?.ai || {};

    if (parsed) {
      const mappedFields = {
        description,
        addExtraExperience: experiences,
        experiences,
        portfolio,
        higherEducation,
        educations,
        childSkills,
        uplodedResumeUrl
      };

      let createOrUpdateResult;
      try {
        createOrUpdateResult = await createOrUpdateListing(mappedFields);
        if (createOrUpdateResult) {
          saveResumeUrlMongoDb({ listingId, userId, email, uplodedResumeUrl });
        }

        const pageType = isPublished ? LISTING_PAGE_PARAM_TYPE_EDIT : LISTING_PAGE_PARAM_TYPE_DRAFT;
        const slug = `${currentUser?.attributes?.profile?.firstName}-${currentUser.attributes?.profile?.lastName}`;
        const listingUUID = createOrUpdateResult?.data?.data?.id?.uuid;

        if (createOrUpdateResult && listingUUID) {
          history.push(
            createResourceLocatorString('EditListingPage', routeConfiguration, {
              id: listingUUID,
              slug,
              type: pageType,
              tab: 'details'
            }, {})
          );
        }

      } catch (err) {
        console.error('Create/Update listing failed:', err);
        createOrUpdateResult = { error: err.response ? err.response.data : err.message };
      }

      dispatch(fetchAIResumeUpdateSuccess({ data: parsed }));
      return parsed;
    }

  } catch (error) {
    console.error('There is some problem in your resume. Please review it and try again.', error);
    dispatch(fetchAIResumeUpdateError({
      message: 'Something went wrong while processing your resume. Please try again.',
      originalError: storableError(error)
    }));
  }
};

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  dispatch(clearUpdatedTab());
  const { id, type, tab } = params;

  if (type === 'new') {
    // No need to listing data when creating a new listing
    return Promise.all([dispatch(fetchCurrentUser())])
      .then(response => {
        const currentUser = getState().user.currentUser;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }
        // if(["details" , "requirements"].includes(tab)){
        //   dispatch(fetchParentJobRoles())
        // }
        return response;
      })
      .catch(e => {
        throw e;
      });
  }

  const payload = { id: new UUID(id) };
  return Promise.all([dispatch(requestShowListing(payload, config)), dispatch(fetchCurrentUser())])
    .then(response => {
      const currentUser = getState().user.currentUser;
      if (currentUser && currentUser.stripeAccount) {
        dispatch(fetchStripeAccount());
      }

      const { accountType } = currentUser && currentUser?.id && currentUser?.attributes?.profile?.publicData || {};

      // half-way complete
      // if ( type === "draft" && tab === "past-efforts" && accountType === "fractionalPro") {
      //   scheduleHalfwayProfileEmail({
      //     id: currentUser?.id?.uuid,
      //     firstName: currentUser?.attributes?.profile?.displayName,
      //     email: currentUser?.attributes?.email,
      //     userType:accountType,
      //     listingId:response[0]?.data?.data?.id?.uuid || '',
      //     listingTitle:response[0]?.data?.data?.attributes?.title || '',
      //   });
      // } else if ( type === "draft" && tab === "how" && accountType === "company") {
      //   scheduleHalfwayProfileEmail({
      //     id: currentUser?.id.uuid,
      //     firstName: currentUser?.attributes?.profile?.displayName,
      //     email: currentUser?.attributes?.email,
      //     userType:accountType,
      //     listingId:response[0]?.data?.data?.id?.uuid || '',
      //     listingTitle:response[0]?.data?.data?.attributes?.title || '',

      //   });
      // }
      // if (["details" , "requirements"].includes(tab)) {
      //   return dispatch(fetchParentJobRoles())
      // }
      // Because of two dispatch functions, response is an array.
      // We are only interested in the response from requestShowListing here,
      // so we need to pick the first one
      const listing = response[0]?.data?.data;
      const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias;
      if (listing && isBookingProcessAlias(transactionProcessAlias)) {
        fetchLoadDataExceptions(dispatch, listing, search, config.localization.firstDayOfWeek);
      }

      return response;
    })
    .catch(e => {
      throw e;
    });
};